import {
  PencilIcon,
  showDate,
  type OwnPlace,
  type PlaceMore,
  type PlaceStop,
  type PlaceTag,
} from '@trippath/shared';
import { useRef, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { dayRange } from '../../lib/countryFacts';

/** A day as the page says it: `9 Mar 2027`. */
export function day(iso: string, language: string): string {
  return showDate(new Date(`${iso.slice(0, 10)}T00:00:00`), language);
}

/** A country's name in the page's language, by its code. */
export function countryName(code: string, language: string): string {
  return String(new Intl.DisplayNames([language], { type: 'region' }).of(code));
}

interface Props {
  place: OwnPlace;
  tags: PlaceTag[];
  more: PlaceMore | null;
  busy: boolean;
  onFavourite: () => void;
  onVisited: () => void;
  onTags: () => void;
  onEdit: () => void;
  onPut: (stop: PlaceStop) => void;
  onMerge: (other: string) => void;
  onApart: (other: string) => void;
  onPhotos: (files: File[]) => void;
}

/**
 * A place of one's own (the `Places` mockup's right side): its photos, name and address, its
 * tags, a favourite or been to, where to put it on a trip, a place kept twice to merge, its notes,
 * opening hours, trips and how it was added.
 */
export function PlaceDetail({
  place,
  tags,
  more,
  busy,
  onFavourite,
  onVisited,
  onTags,
  onEdit,
  onPut,
  onMerge,
  onApart,
  onPhotos,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const files = useRef<HTMLInputElement>(null);
  const own = tags.filter((tag) => place.tags.includes(tag.id));
  const similar = place.similar;
  const [first, second, third] = place.photos;
  const rest = place.photos.length - 3;
  const where = [place.address, place.city].filter((one) => one !== null).join(' · ');
  const stop = more?.stops[0];
  const there = stop && place.trips.some((on) => on.stop === stop.stop);
  const photo = (src: string, at: number, extra?: string) => (
    <a key={src} href={src} target="_blank" rel="noreferrer" className="places-photo">
      <img
        src={src}
        alt={t('myPlaces.photoOf', { n: at + 1, count: place.photos.length, name: place.name })}
      />
      {extra !== undefined && <span>{extra}</span>}
    </a>
  );
  return (
    <>
      <div className="places-sec">
        {first !== undefined && (
          <div
            className={`places-photos places-photos-${String(Math.min(3, place.photos.length))}`}
          >
            {photo(
              first,
              0,
              place.photos.length > 1 ? `1 / ${String(place.photos.length)}` : undefined,
            )}
            {second !== undefined && photo(second, 1)}
            {third !== undefined &&
              photo(third, 2, rest > 0 ? t('myPlaces.morePhotos', { count: rest }) : undefined)}
          </div>
        )}
        <div className="places-title">
          <div>
            <h2>{place.name}</h2>
            {where !== '' && <p className="places-small quiet">{where}</p>}
          </div>
          <button
            type="button"
            className="icon-button icon-button-small"
            onClick={onEdit}
            aria-label={`${t('myPlaces.edit')}: ${place.name}`}
            title={t('myPlaces.edit')}
          >
            <PencilIcon />
          </button>
        </div>
        <div className="places-filters">
          {own.map((tag) => (
            <span
              key={tag.id}
              className="places-tag"
              // Its colour round it; its words that colour drawn toward the text's, to read in
              // either theme.
              style={{ '--tag': tag.colour } as CSSProperties}
            >
              {tag.emoji} {tag.name}
            </span>
          ))}
          <button type="button" className="link-button places-small" onClick={onTags}>
            + {t('myPlaces.addTag')}
          </button>
        </div>
        <div className="places-toggles">
          <button
            type="button"
            className="places-toggle places-toggle-fav"
            aria-pressed={place.favourite}
            disabled={busy}
            onClick={onFavourite}
          >
            ★ {t('myPlaces.markFavourite')}
          </button>
          <button
            type="button"
            className="places-toggle"
            aria-pressed={place.visitedOn !== null}
            disabled={busy}
            onClick={onVisited}
          >
            ✓{' '}
            {place.visitedOn === null
              ? t('myPlaces.markVisited')
              : `${t('myPlaces.visited')} ${day(place.visitedOn, lang)}`}
          </button>
        </div>
        {stop !== undefined &&
          (there === true ? (
            <p className="places-small">
              ✓ {t('myPlaces.onTrip', { trip: stop.tripName, city: stop.city })}
            </p>
          ) : (
            <button
              type="button"
              className="button places-add"
              disabled={busy}
              onClick={() => {
                onPut(stop);
              }}
            >
              {t('myPlaces.addTo', {
                trip: stop.tripName,
                city: stop.city,
                days: dayRange(stop.start, stop.end, lang),
              })}
            </button>
          ))}
        {more !== null && stop === undefined && (
          <p className="places-small quiet">{t('myPlaces.noStop')}</p>
        )}
      </div>
      {similar !== null && (
        <div className="places-sec">
          <div className="places-dup" role="status">
            <b>{t('myPlaces.similar')}</b>
            <span>
              {t('myPlaces.similarSays', {
                name: similar.name,
                metres: similar.metres,
                day: day(similar.createdAt, lang),
                source: t(`myPlaces.source.${similar.source}`),
              })}
            </span>
            <div className="places-row">
              <button
                type="button"
                className="button button-small button-primary"
                disabled={busy}
                onClick={() => {
                  onMerge(similar.id);
                }}
              >
                {t('myPlaces.merge')}
              </button>
              <button
                type="button"
                className="button button-small"
                disabled={busy}
                onClick={() => {
                  onApart(similar.id);
                }}
              >
                {t('myPlaces.keepApart')}
              </button>
            </div>
            <span className="places-small">{t('myPlaces.mergeHow')}</span>
          </div>
        </div>
      )}
      <div className="places-sec">
        <dl className="places-kv">
          {place.note !== null && (
            <>
              <dt>{t('myPlaces.note')}</dt>
              <dd>{place.note}</dd>
            </>
          )}
          <dt>{t('myPlaces.hours')}</dt>
          <dd>
            {more === null
              ? t('myPlaces.asking')
              : more.hours === null
                ? t('myPlaces.noHours')
                : t('myPlaces.hoursFrom', { hours: more.hours })}
          </dd>
          <dt>{t('myPlaces.trips')}</dt>
          <dd>
            {place.trips.length === 0
              ? t('myPlaces.noTrips')
              : place.trips.map((on, at) => (
                  <span key={`${on.trip}-${on.stop}`}>
                    {at > 0 && <br />}
                    <Link to={`/trips/${on.trip}/stops/${on.stop}`}>{on.tripName}</Link> ·{' '}
                    {on.city}
                    {on.day !== null && `, ${day(on.day, lang)}`}
                  </span>
                ))}
          </dd>
          <dt>{t('myPlaces.added')}</dt>
          <dd>
            {t('myPlaces.addedHow', {
              day: day(place.createdAt, lang),
              source: t(`myPlaces.source.${place.source}`),
            })}
          </dd>
        </dl>
        <input
          ref={files}
          type="file"
          accept="image/*"
          multiple
          hidden
          aria-label={t('myPlaces.addPhotos')}
          onChange={(event) => {
            onPhotos([...(event.target.files as FileList)]);
            event.target.value = '';
          }}
        />
        <button
          type="button"
          className="button places-start"
          disabled={busy}
          onClick={() => files.current?.click()}
        >
          {t('myPlaces.addPhotos')}
        </button>
      </div>
    </>
  );
}
